import axios, { AxiosHeaders } from 'axios';
import type { AxiosInstance } from 'axios';
import type {
  Vehicle,
  VehicleListResponse,
  VehicleQueryParams,
  Assignment,
  AssignmentCreate,
  AssignmentUpdate,
  AssignmentListResponse,
  FilterCounts,
  ClientViewDealersResponse,
  ClientViewMapPointsResponse,
  ClientViewDealerCardsResponse,
  ClientViewDealerDetail,
  ClientViewVinsResponse,
  ClientSavedView,
  ClientSavedViewsResponse,
  ClientSearchResult,
  ClientParentSearchResult,
  ClientVinSearchResult,
  ClientViewParams,
  CallListParams,
  ClientViewCseRepOption,
  VinStatusDetail,
  ClientTeamViewSet,
  CallListResponse,
  ClientStatusOptionsResponse,
  ClientStatusRecord,
  ClientStatusDetails,
  SalesViewParams,
  SalesClientSearchResult,
  SalesParentSearchResult,
  SalesViewClientsResponse,
  SalesViewClientDetail,
  SalesClientSummary,
  SalesViewCohort,
  SalesPriceBandsBlock,
  SalesMarketBandsBlock,
  BandKind,
  SalesTargetListsResponse,
  SalesTargetListCreateResponse,
  SalesTargetUntargetResponse,
  SalesViewSummary,
  SalesViewMapPointsResponse,
  SalesScoreCatalog,
  SalesFilterOptions,
  SalesCloseOutRequest,
  SalesCloseOutResponse,
  SalesPlanCurrentResponse,
  SalesPlanDiffResponse,
  SalesPlanDigestResponse,
  SalesPlanRecapResponse,
  SalesPlanKickoffResponse,
  SalesPlanNearbyResponse,
  PlanEdit,
  PlanEditsResponse,
  PlanSettings,
  StartAddress,
  BrokerVehiclePointsResponse,
  BrokerSliceMapResponse,
  BrokerBuyerMatchesResponse,
  CohortInsightsResponse,
  BrokerBoardBuyersResponse,
  BrokerBuyerVinsPageResponse,
  BrokerBoardClustersResponse,
  BrokerClusterVinsPageResponse,
  BrokerBasketSuggestion,
  BrokerSliceKind,
  BrokerSliceVinsResponse,
  BrokerChipFilterParams,
  BrokerSettings,
  BrokerSettingsResponse,
  BrokerSettingsPreview,
  BrokerFactorImpactResponse,
  BrokerFitImpactResponse,
  BrokerRecommendImpactResponse,
  BrokerFitRankedWeights,
  BrokerCutoffSolution,
  BrokerRankedWeights,
  BrokerBuyerSearchResult,
  BrokerParentSearchResult,
  BrokerContactOverride,
  BrokerSellerGroupSearchResult,
  BrokerSellerSearchResult,
  BrokerVinSearchResult,
  BrokerSavedView,
  BrokerSavedViewsResponse,
  OutreachDraftRequest,
  OutreachDraftResponse,
} from './types';

// Use relative URL - works with nginx proxy in production and Vite proxy in development
const API_BASE_URL = '/api';

// ========== Auth Types ==========

export interface TerritoryAccess {
  is_admin: boolean;
  access_level: 'admin' | 'grandparent' | 'parent' | 'territory' | null;
  territory_value: string | null;
}

export interface UserSession {
  email: string;
  role: string;
  display_name?: string;
  theme_preference?: string;
  is_admin: boolean;
  territory_access: TerritoryAccess;
  allowed_regions: string[]; // Deprecated, kept for compatibility
  feature_flags?: string[];
  auth_source?: 'entra' | 'legacy';
  provisioned?: boolean; // false when Entra-authenticated but not yet in USERS table
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ProfileUpdateRequest {
  display_name?: string;
  theme_preference?: string;
}

export interface CheckEmailResponse {
  exists: boolean;
  needs_password_setup: boolean;
  display_name?: string;
}

export interface AdminUser {
  email: string;
  role: string;
  managed_by?: string;
  active: boolean;
  display_name?: string;
  last_login?: string;
  created_at?: string;
  updated_at?: string;
  feature_flags?: string[];
}

export interface CreateUserRequest {
  email: string;
  role?: string;
  managed_by?: string;
  display_name?: string;
  active?: boolean;
  feature_flags?: string[];
}

export interface UpdateUserRequest {
  role?: string;
  managed_by?: string;
  display_name?: string;
  active?: boolean;
  password?: string;
  feature_flags?: string[];
}

export interface AdminPermissions {
  can_view_users: boolean;
  can_create_users: boolean;
  can_edit_users: boolean;
  can_delete_users: boolean;
  can_reset_passwords: boolean;
  can_bulk_export: boolean;
  is_admin: boolean;
  is_manager: boolean;
}

/** One cron-refreshed dataset row for the admin Data Refresh tab. */
export interface CacheDataset {
  key: string;
  label: string;
  description: string;
  cadence: string;
  /** ISO UTC of the last refresh SIFT itself ran (pull from Snowflake into Parquet). */
  last_refreshed_at: string | null;
  /**
   * ISO of the upstream Snowflake `ingest_ts` the current data carries — the same
   * value the Client/Broker View "data last updated" labels show. Null for locally
   * computed datasets (see `computed`).
   */
  source_last_refreshed_at: string | null;
  exists: boolean;
  /** Refresh group this dataset belongs to (datasets in a group refresh together). */
  group_key: string;
  group_label: string;
  group_description: string;
  /** 1-based order the dataset must be refreshed in within its group. */
  step: number;
  step_count: number;
  /** Dataset keys this one is built from (may live in another group). */
  depends_on: string[];
  /** False when the dataset may only be refreshed as part of its group. */
  individually_refreshable: boolean;
  /** True when derived locally from other datasets (no upstream source timestamp). */
  computed: boolean;
}

/** A set of datasets that must be refreshed together, in `datasets` step order. */
export interface CacheDatasetGroup {
  key: string;
  label: string;
  description: string;
  datasets: CacheDataset[];
}

/** One step of a background refresh job. */
export interface CacheRefreshJobStep {
  dataset: string;
  label: string;
  step: number;
  status: 'pending' | 'running' | 'done' | 'failed';
  row_count: number | null;
  error: string | null;
}

/**
 * A background data-refresh job. A refresh takes minutes, so the admin UI starts
 * one of these and polls its progress instead of holding an HTTP request open.
 */
export interface CacheRefreshJob {
  /** 'all', a group key, a dataset key, or 'deploy' for the post-deploy refresh. */
  scope: string;
  label: string;
  status: 'running' | 'done' | 'failed';
  started_at: string;
  finished_at: string | null;
  steps: CacheRefreshJobStep[];
  error?: string;
}

/** One run of the Plan My Week batch (admin Data Refresh tab). */
export interface SalesPlanBatchJob {
  plan_week_start: string;
  /** Rebuilt every already-frozen week rather than keeping it. */
  rebuild?: boolean;
  status: 'running' | 'done' | 'failed';
  started_at: string | null;
  finished_at: string | null;
  error?: string;
  report: {
    plan_week_start?: string;
    generated: number;
    /** Frozen weeks replaced by a fresh build (a rebuild run, or a rep's changed capacity). */
    rebuilt?: number;
    /** Reps whose week was ALREADY frozen, or who have no clients to plan. */
    skipped: number;
    failed: number;
    /** Always 0 here: the admin run never delivers. */
    delivered?: number;
    duration_seconds?: number;
    errors?: string[];
  } | null;
}

/** OPS-1 usage event as sent to POST /api/usage/events (the user is stamped server-side). */
export interface UsageEventPayload {
  view: string;
  feature: string;
  action: string;
  seller_uid?: string | null;
  vin?: string | null;
  buyer_uid?: string | null;
  meta?: Record<string, unknown>;
  ts: string;
  app_version?: string;
}

const TOKEN_STORAGE_KEY = 'sift_token';
const ENTRA_AUTH_HEADER = 'X-Sift-Authorization';

// ========== Agent Types ==========

export interface AgentSelection {
  seller_id?: string;
  vin?: string;
  /** Sales View focuses a CLIENT rather than a dealer/VIN. One shape serves both surfaces. */
  client_id?: string;
}

/**
 * The user's active board scope. Mirrors the board's filters so the agent ranks the SAME
 * role-scoped set the user sees (instead of querying globally). Client View fields come first,
 * Sales View fields after; each surface sends only its own.
 */
export interface AgentScope {
  managers?: string[];
  market_centers?: string[];
  rds_territories?: string[];
  /** Client View CSE Rep filter (CSE territory owner). */
  cse_reps?: string[];
  /** Carousel grain; sent only as 'quarter', which makes tiers and To Core read the quarter. */
  metric_grain?: string;
  seller_names?: string[];
  /** Board filters beyond territory. Without these the agent answers over the rep's whole
   *  territory while the rep is looking at a filtered subset. */
  vin_statuses?: string[];
  priorities?: string[];
  tiers?: string[];
  metric_filters?: Record<string, { min: number | null; max: number | null }>;
  inspector_zip?: string;
  inspector_seller_id?: string;
  radius_miles?: number;

  /** Sales View board filters. */
  cse_manager?: string[];
  cse_director?: string[];
  cse_territory?: string[];
  cse_territory_owner?: string[];
  market_center?: string[];
  dealer_group?: string[];
  /** Health tier. Singular, and NOT the same field as Client View's `tiers` above — the two
   *  surfaces name it differently and the backend picks them apart by that. */
  tier?: string[];
  reason_group?: string[];
  engagement_band?: string[];
  band?: string[];
  state?: string[];
  city?: string[];
  client_search?: string;
  /** Exact client picks from the Sales View search dropdown. */
  client_token?: string[];
  /** Opportunity codes (CSE-15) and leader target list ids (CSE-17). */
  opportunity?: string[];
  target_list?: string[];
  went_dark?: boolean;
  target_missing?: boolean;
}

/**
 * Which Mia is being asked. Both are Mia to the user; the key selects the tool surface and brief
 * server-side (backend app/agent/registry.py).
 */
export type AgentKey = 'rds' | 'sales';

export interface AgentChatRequest {
  message: string;
  selection?: AgentSelection;
  scope?: AgentScope;
  /**
   * Frontend-owned chat-thread id. Distinct from the login session: reset rotates it and the
   * backend archives the ended conversation to long-term storage under this id.
   */
  conversation_id?: string;
  /** Omitted means the RDS surface: Client View predates the registry. */
  agent_key?: AgentKey;
  /** Curated workflow that opened this thread, archived so a tagged thread is resumable. */
  workflow?: string;
}

/** A UI action directive emitted by the agent (validated server-side against an allowlist). */
export interface AgentAction {
  type:
    | 'navigate_dealer'
    | 'navigate_vin'
    | 'navigate_client'
    | 'apply_filter'
    | 'set_view'
    | 'set_sort'
    | 'highlight'
    | 'back'
    | 'reset'
    | 'suggest'
    | 'propose_plan_edit';
  seller_id?: string;
  vin?: string;
  client_id?: string;
  priority?: string;
  tier?: string;
  reason_group?: string;
  band?: string;
  target?: string;
  /** For type==='set_view': which Client View board mode to show. */
  view?: 'listing_actions' | 'call_list';
  /** For type==='set_sort': which Sales View carousel ranking to apply. */
  sort?: 'health' | 'contact' | 'both';
  /** For type==='suggest': the chip's button text. */
  label?: string;
  /** For type==='suggest': the chat message the chip sends when clicked. */
  prompt?: string;
  /* For type==='propose_plan_edit'. Mia cannot change the plan; she offers a change and the
   * rep's click is what writes it. There is deliberately no button label here -- the UI derives
   * the wording from `action`, so a proposal cannot describe itself as something else. */
  action?: 'added' | 'dropped' | 'moved' | 'reordered' | 'anchored' | 'mode_changed';
  client_name?: string;
  contact_mode?: 'VISIT' | 'CALL';
  to_trip?: number;
  to_stop?: number;
  /** One short line on why the change was proposed. */
  reason?: string;
}

export type AgentStreamEvent =
  | { type: 'delta'; text: string }
  /** Drop the text streamed since the last tool step: a blocked draft that is being retried. */
  | { type: 'retract' }
  | { type: 'actions'; actions: AgentAction[] }
  | { type: 'tool'; names: string[] }
  | { type: 'error'; message: string }
  | { type: 'done'; turn_id?: string };

/** Passive user rating on one agent response. */
export type AgentFeedbackRating = 'good' | 'bad';

export const AGENT_WAF_BLOCK_MESSAGE =
  "Your message was blocked by the site's security filter. Try rewording it.";

/**
 * Serialise a chat request with `message` LAST.
 *
 * The WAF's SQLiExtendedPatterns_BODY rule reads the body as raw text, so any message containing
 * " and " followed by our own `"selection"` key matched as `AND ... SELECT`: every tier chip
 * ("..., and why?") and dealer chip was blocked with a 403 (nonprod, 2026-10-02). With the
 * message last, only the user's own words follow it, and those are still inspected in full.
 */
export function agentChatBody(body: AgentChatRequest): string {
  const { message, ...rest } = body;
  return JSON.stringify({ ...rest, message });
}

class SiftApiClient {
  private client: AxiosInstance;
  private accessToken: string | null = null;
  private tokenRefresher: (() => Promise<string | null>) | null = null;

  constructor(baseURL: string = API_BASE_URL) {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 60000, // 60 second timeout for all API calls
      withCredentials: true, // Required for HttpOnly cookies
    });

    // Attach Bearer token from localStorage as fallback for lost cookies
    this.client.interceptors.request.use((config) => {
      const headers = AxiosHeaders.from(config.headers);
      if (this.accessToken) {
        headers.set(ENTRA_AUTH_HEADER, `Bearer ${this.accessToken}`);
        headers.set('Authorization', `Bearer ${this.accessToken}`);
      } else {
        const token = localStorage.getItem(TOKEN_STORAGE_KEY);
        if (token) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      }
      config.headers = headers;
      return config;
    });

    // §5.2 a 401 mid-task was terminal: nothing re-minted the session, so the
    // user saw "invalid access token" and had to reload. Re-acquire once and
    // replay the request. Deliberately ONE attempt, gated on a per-request flag,
    // so a genuinely revoked session surfaces its 401 instead of looping.
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const config = error?.config as (typeof error.config & { _sift_retried?: boolean }) | undefined;
        if (status !== 401 || !config || config._sift_retried || !this.tokenRefresher) {
          return Promise.reject(error);
        }
        config._sift_retried = true;
        let refreshed: string | null = null;
        try {
          refreshed = await this.tokenRefresher();
        } catch {
          return Promise.reject(error);
        }
        if (!refreshed) return Promise.reject(error);
        this.accessToken = refreshed;
        return this.client.request(config);
      },
    );
  }

  private storeToken(payload: Record<string, unknown>): void {
    const token = typeof payload.token === 'string' ? payload.token : null;
    if (token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      return;
    }
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  private clearToken(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  /** §5.2 how to re-mint an expired session; see the 401 interceptor. */
  setTokenRefresher(refresher: (() => Promise<string | null>) | null): void {
    this.tokenRefresher = refresher;
  }

  clearAccessToken(): void {
    this.accessToken = null;
  }

  // ========== Auth Endpoints ==========

  /**
   * Check if email exists and needs password setup
   */
  async checkEmail(email: string): Promise<CheckEmailResponse> {
    const response = await this.client.post<CheckEmailResponse>('/auth/check-email', { email });
    return response.data;
  }

  /**
   * Set up initial password for new users
   */
  async setupPassword(email: string, newPassword: string): Promise<UserSession> {
    const response = await this.client.post<UserSession>('/auth/setup-password', { 
      email, 
      new_password: newPassword 
    });
    this.storeToken(response.data as unknown as Record<string, unknown>);
    return response.data;
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<UserSession> {
    const response = await this.client.post<UserSession>('/auth/login', { email, password });
    this.storeToken(response.data as unknown as Record<string, unknown>);
    return response.data;
  }

  /**
   * Get current user session
   */
  async me(accessToken?: string | null): Promise<UserSession> {
    if (accessToken !== undefined) {
      this.setAccessToken(accessToken);
    }
    const response = await this.client.get<UserSession>('/auth/me');
    return response.data;
  }

  /**
   * Change current user's password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.client.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    await this.client.post('/auth/logout');
    this.clearToken();
    this.clearAccessToken();
  }

  /**
   * Update current user's profile
   */
  async updateProfile(data: ProfileUpdateRequest): Promise<UserSession> {
    const response = await this.client.patch<UserSession>('/auth/me', data);
    this.storeToken(response.data as unknown as Record<string, unknown>);
    return response.data;
  }

  // ========== Admin Endpoints ==========

  /**
   * Get current user's admin panel permissions
   */
  async getAdminPermissions(): Promise<AdminPermissions> {
    const response = await this.client.get<AdminPermissions>('/admin/permissions');
    return response.data;
  }

  /**
   * Force-refresh the primary datasets from Snowflake (admin only): the
   * vehicle/VIN data, seller stats, buyer activity + engagement, and the
   * buyer-match precompute. Used by the "Refresh Data" control to pull in a
   * fresh Snowflake load without waiting for the scheduled background refresh.
   */
  async refreshDataCache(): Promise<{
    vehicles: { success: boolean; row_count?: number };
    seller_stats: { success: boolean; row_count?: number };
    buyer_activity: { success: boolean; row_count?: number };
    buyer_engagement?: { success: boolean; row_count?: number };
    buyer_matches?: { success: boolean; row_count?: number };
  }> {
    const response = await this.client.post<{
      vehicles: { success: boolean; row_count?: number };
      seller_stats: { success: boolean; row_count?: number };
      buyer_activity: { success: boolean; row_count?: number };
      buyer_engagement?: { success: boolean; row_count?: number };
      buyer_matches?: { success: boolean; row_count?: number };
    }>('/admin/cache/refresh-data');
    return response.data;
  }

  /** List the cron-refreshed datasets, grouped by refresh group (admin Data Refresh tab). */
  async listCacheDatasets(): Promise<{ groups: CacheDatasetGroup[]; datasets: CacheDataset[] }> {
    const response = await this.client.get<{ groups: CacheDatasetGroup[]; datasets: CacheDataset[] }>('/admin/cache/datasets');
    return response.data;
  }

  /**
   * Start a BACKGROUND refresh and get its initial job state (admin only). `scope`
   * is 'all', a group key, or an individually-refreshable dataset key. Returns
   * immediately — poll `getCacheRefreshJob` for progress, since a refresh runs far
   * longer than the client's HTTP timeout.
   */
  async startCacheRefresh(scope: string): Promise<{ job: CacheRefreshJob }> {
    const response = await this.client.post<{ job: CacheRefreshJob }>(
      '/admin/cache/refresh-job',
      null,
      { params: { scope } },
    );
    return response.data;
  }

  /** Poll the current (or most recent) background refresh job (admin only). */
  async getCacheRefreshJob(): Promise<{ job: CacheRefreshJob | null; building?: string[] }> {
    const response = await this.client.get<{ job: CacheRefreshJob | null; building?: string[] }>(
      '/admin/cache/refresh-job',
    );
    return response.data;
  }

  /**
   * Start building a week for EVERY CSE territory (admin only). Returns as soon as
   * the run is queued — it takes over a minute, longer than the request may live —
   * so poll `getSalesPlanJob` for the result.
   *
   * NEVER delivers: the backend forces `deliver=false`, so this cannot mail reps
   * however often it is run.
   *
   * `rebuild` (next week only) rebuilds every already-frozen week with the current planner
   * and makes it the plan of record, keeping the reps' edits.
   */
  async generateSalesPlans(
    week: 'next' | 'current' = 'next',
    rebuild = false,
  ): Promise<{ job: SalesPlanBatchJob }> {
    const response = await this.client.post<{ job: SalesPlanBatchJob }>(
      '/admin/sales-plan/generate',
      null,
      { params: rebuild ? { week, rebuild: true } : { week } },
    );
    return response.data;
  }

  /** Poll the current (or most recent) plan generation run (admin only).
   *
   * `pending_writes` is plans queued but not yet in Snowflake -- a run reports
   * "generated" well before the last row lands.
   */
  async getSalesPlanJob(): Promise<{ job: SalesPlanBatchJob | null; pending_writes: number }> {
    const response = await this.client.get<{
      job: SalesPlanBatchJob | null; pending_writes: number;
    }>(
      '/admin/sales-plan/generate',
    );
    return response.data;
  }

  /**
   * List all users (admin or manager)
   */
  async listUsers(): Promise<{ users: AdminUser[]; total: number }> {
    const response = await this.client.get<{ users: AdminUser[]; total: number }>('/admin/users');
    return response.data;
  }

  /**
   * Create a new user (admin only)
   */
  async createUser(data: CreateUserRequest): Promise<AdminUser> {
    const response = await this.client.post<AdminUser>('/admin/users', data);
    return response.data;
  }

  /**
   * Get a specific user (admin only)
   */
  async getUser(email: string): Promise<AdminUser> {
    const response = await this.client.get<AdminUser>(`/admin/users/${encodeURIComponent(email)}`);
    return response.data;
  }

  /**
   * Update a user (admin only)
   */
  async updateUser(email: string, data: UpdateUserRequest): Promise<AdminUser> {
    const response = await this.client.patch<AdminUser>(
      `/admin/users/${encodeURIComponent(email)}`,
      data
    );
    return response.data;
  }

  /**
   * Delete a user (admin only)
   */
  async deleteUser(email: string): Promise<void> {
    await this.client.delete(`/admin/users/${encodeURIComponent(email)}`);
  }

  /**
   * Reset a user's password (admin only)
   * Sets password_hash to "reset" so user must set up new password on next login
   */
  async resetUserPassword(email: string): Promise<AdminUser> {
    const response = await this.client.post<AdminUser>(
      `/admin/users/${encodeURIComponent(email)}/reset-password`
    );
    return response.data;
  }

  /**
   * Export users as CSV (admin only). Fetched through the client so the Entra header is
   * sent; a plain navigation carries only the legacy cookie, which nonprod/prod reject.
   */
  async exportUsersCSV(): Promise<void> {
    const response = await this.client.get<Blob>('/admin/users/export/csv', { responseType: 'blob' });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sift_users.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  // ========== Vehicle Endpoints ==========

  /**
   * Get a paginated list of vehicles with optional filtering
   */
  async getVehicles(params?: VehicleQueryParams, impersonateRole?: string): Promise<VehicleListResponse> {
    const queryParams = impersonateRole 
      ? { ...params, impersonate_role: impersonateRole }
      : params;
    const response = await this.client.get<VehicleListResponse>('/vehicles/', { params: queryParams });
    return response.data;
  }

  /**
   * Get filter options for dropdowns. Pass `broker` to scope the options to the
   * Broker-View INCLUDE board (only values that appear on that board).
   */
  async getFilterOptions(impersonateRole?: string, broker?: boolean): Promise<{
    managers: string[];
    market_centers: string[];
    rds_territories: string[];
    sellers: string[];
    auctions: string[];
    mapping: {
      manager: string;
      territory_market_center: string;
      rds_territory: string;
      seller_name: string;
    }[];
  }> {
    const params: Record<string, string | boolean> = {};
    if (impersonateRole) params.impersonate_role = impersonateRole;
    if (broker) params.broker = true;
    const response = await this.client.get('/vehicles/filters', { params });
    return response.data;
  }

  /**
   * Get distinct values for a specific column (for column filters)
   * @param valueSearch - Optional search term to filter the column values themselves
   */
  async getColumnDistinctValues(
    column: string,
    filters?: {
      manager?: string;
      market_center?: string;
      rds_territory?: string;
      seller_name?: string;
      stage?: string;
      inspect_status?: string;
      days_listed_ranges?: string;
      days_expired_ranges?: string;
      days_since_inspection_ranges?: string;
      user_status?: string;
      search?: string;
    },
    impersonateRole?: string,
    valueSearch?: string
  ): Promise<{ column: string; values: string[]; total_count: number; truncated: boolean }> {
    const params = {
      ...filters,
      ...(impersonateRole ? { impersonate_role: impersonateRole } : {}),
      ...(valueSearch ? { value_search: valueSearch } : {})
    };
    const response = await this.client.get(`/vehicles/column-values/${column}`, { params });
    return response.data;
  }

  /**
   * Get a single vehicle by VIN
   */
  async getVehicleByVin(vin: string): Promise<Vehicle> {
    const response = await this.client.get<Vehicle>(`/vehicles/${vin}`);
    return response.data;
  }

  // ========== Analytics Endpoints ==========

  /**
   * Get filter counts and statistics for the dataset
   */
  async getFilterCounts(params?: VehicleQueryParams): Promise<FilterCounts> {
    const response = await this.client.get<FilterCounts>('/analytics/filter-counts', { params });
    return response.data;
  }

  // ========== Assignment Endpoints ==========

  /**
   * Get all assignments
   */
  async getAssignments(): Promise<AssignmentListResponse> {
    const response = await this.client.get<AssignmentListResponse>('/assignments/');
    return response.data;
  }

  /**
   * Get a single assignment by ID
   */
  async getAssignment(id: number): Promise<Assignment> {
    const response = await this.client.get<Assignment>(`/assignments/${id}`);
    return response.data;
  }

  /**
   * Create a new assignment (or update if VIN already assigned)
   */
  async createAssignment(data: AssignmentCreate): Promise<Assignment> {
    const response = await this.client.post<Assignment>('/assignments/', data);
    return response.data;
  }

  /**
   * Update an existing assignment
   */
  async updateAssignment(id: number, data: AssignmentUpdate): Promise<Assignment> {
    const response = await this.client.put<Assignment>(`/assignments/${id}`, data);
    return response.data;
  }

  /**
   * Delete an assignment
   */
  async deleteAssignment(id: number): Promise<void> {
    await this.client.delete(`/assignments/${id}`);
  }

  // ========== VIN Status Endpoints ==========

  /**
   * Get status for a single VIN
   */
  async getVinStatus(vin: string): Promise<{ vin: string; status: string | null; user_email: string }> {
    const response = await this.client.get(`/vin-status/${vin}`);
    return response.data;
  }

  /**
   * Get statuses for multiple VINs
   */
  async getVinStatuses(vins: string[]): Promise<{ statuses: Record<string, string | null>; count: number }> {
    const response = await this.client.get('/vin-status/', {
      params: { vins: vins.join(',') }
    });
    return response.data;
  }

  /**
   * Set status for a single VIN
   */
  async setVinStatus(
    vin: string,
    status: string,
    reasonText?: string | null,
  ): Promise<{ vin: string; status: string; user_email: string; reason_text?: string | null }> {
    const response = await this.client.post('/vin-status/', { vin, status, reason_text: reasonText || null });
    return response.data;
  }

  /**
   * Status + reason for VINs that have a status (RDS-2.3). Chunked to the endpoint's
   * 500-VIN limit; VINs with no status are simply absent from the result.
   */
  async getVinStatusDetails(vins: string[]): Promise<Record<string, VinStatusDetail>> {
    const out: Record<string, VinStatusDetail> = {};
    for (let i = 0; i < vins.length; i += 500) {
      const chunk = vins.slice(i, i + 500);
      if (chunk.length === 0) continue;
      const response = await this.client.get<{ statuses: Record<string, VinStatusDetail> }>(
        '/vin-status/details', { params: { vins: chunk.join(',') } },
      );
      Object.assign(out, response.data.statuses || {});
    }
    return out;
  }

  /**
   * Set status for multiple VINs (bulk)
   */
  async setVinStatusBulk(
    vins: string[],
    status: string,
    reasonText?: string | null,
  ): Promise<{ statuses: Record<string, string>; count: number }> {
    const response = await this.client.post('/vin-status/bulk', { vins, status, reason_text: reasonText || null });
    return response.data;
  }

  /**
   * Delete (clear) status for a single VIN
   */
  async deleteVinStatus(vin: string): Promise<{ vin: string; status: null; user_email: string }> {
    const response = await this.client.delete(`/vin-status/${vin}`);
    return response.data;
  }

  /**
   * Delete (clear) statuses for multiple VINs (bulk)
   */
  async deleteVinStatusBulk(vins: string[]): Promise<{ statuses: Record<string, null>; count: number }> {
    const response = await this.client.delete('/vin-status/bulk', {
      params: { vins: vins.join(',') }
    });
    return response.data;
  }

  /**
   * Get status statistics/counts
   */
  async getVinStatusStats(): Promise<Record<string, number>> {
    const response = await this.client.get('/vin-status/stats/summary');
    return response.data;
  }

  /**
   * §10-D: set the broker-group VIN status (independent of the RDS user status).
   * Broker/admin only; the group is resolved server-side from the caller's USERS row.
   */
  async setBrokerVinStatus(vin: string, status: string): Promise<{ vin: string; status: string; group: string }> {
    const response = await this.client.post(`/broker-view/vins/${encodeURIComponent(vin)}/status`, { status });
    return response.data;
  }

  /**
   * §6.5: read the broker-group status for one VIN. The shared VIN-detail payload
   * carries no broker status, so the pop-out's control resolves its own.
   */
  async getBrokerVinStatus(vin: string, signal?: AbortSignal): Promise<{
    vin: string; status: string | null; by: string | null; at: string | null; group: string;
  }> {
    const response = await this.client.get(
      `/broker-view/vins/${encodeURIComponent(vin)}/status`, { signal });
    return response.data;
  }

  /**
   * §10-D: clear the broker-group VIN status for a VIN. Broker/admin only.
   */
  async clearBrokerVinStatus(vin: string): Promise<{ vin: string; status: null; group: string }> {
    const response = await this.client.delete(`/broker-view/vins/${encodeURIComponent(vin)}/status`);
    return response.data;
  }

  /**
   * §10-D: set the broker-group status for many VINs at once. Broker/admin only.
   */
  async setBrokerVinStatusBulk(vins: string[], status: string): Promise<{ vins: string[]; status: string; group: string; count: number }> {
    const response = await this.client.post('/broker-view/vins/status/bulk', { vins, status });
    return response.data;
  }

  /**
   * §10-D: clear the broker-group status for many VINs at once. Broker/admin only.
   */
  async clearBrokerVinStatusBulk(vins: string[]): Promise<{ vins: string[]; status: null; group: string; count: number }> {
    const response = await this.client.delete('/broker-view/vins/status/bulk', {
      params: { vins: vins.join(',') },
    });
    return response.data;
  }

  // ========== Client View Endpoints ==========

  /**
   * Get dealers for the Client View tab with extended KPIs, Core Clients metadata,
   * priority counts (vin_action_filter), and SFDC summary.
   *
   * Accepts an optional AbortSignal so callers can cancel an in-flight request when
   * a newer one supersedes it (prevents a slow earlier response from overwriting a
   * fresher filtered result).
   */
  async getClientViewDealers(
    params?: ClientViewParams,
    signal?: AbortSignal,
  ): Promise<ClientViewDealersResponse> {
    const response = await this.client.get<ClientViewDealersResponse>('/client-view/dealers', { params, signal });
    return response.data;
  }

  /**
   * Thin map/list payload for the full dealer set (geo + name + tier +
   * priority_counts only). Drives both the map and the carousel list; the
   * KPI-heavy card bodies are fetched per visible page via
   * `getClientViewDealerCards`.
   */
  /** CSE Rep dropdown source, with the manager / market center / RDS territory it cascades on. */
  async getClientViewCseRepOptions(): Promise<ClientViewCseRepOption[]> {
    const response = await this.client.get<{ mapping: ClientViewCseRepOption[] }>('/client-view/cse-rep-options');
    return response.data.mapping || [];
  }

  async getClientViewMapPoints(
    params?: ClientViewParams,
    signal?: AbortSignal,
  ): Promise<ClientViewMapPointsResponse> {
    const response = await this.client.get<ClientViewMapPointsResponse>('/client-view/map-points', { params, signal });
    return response.data;
  }

  /**
   * Batch-fetch KPI card bodies for the visible carousel page (max 200 ids).
   * The frontend merges each body with its thin map-point to render the card.
   */
  async getClientViewDealerCards(
    sellerIds: string[],
    signal?: AbortSignal,
    metricGrain?: string,
  ): Promise<ClientViewDealerCardsResponse> {
    if (sellerIds.length === 0) return { cards: [] };
    const response = await this.client.get<ClientViewDealerCardsResponse>('/client-view/dealer-cards', {
      params: { ids: sellerIds.join(','), metric_grain: metricGrain },
      signal,
    });
    return response.data;
  }

  /**
   * Get full dealer detail for Pop-out 1 (current+prior SIFT metrics, SFDC block, recent_notes).
   */
  async getClientViewDealerDetail(sellerId: string): Promise<ClientViewDealerDetail> {
    const response = await this.client.get<ClientViewDealerDetail>(
      `/client-view/dealers/${encodeURIComponent(sellerId)}`,
    );
    return response.data;
  }

  /** Client View saved views — the user's PERSONAL set + the GLOBAL shared set,
   * plus whether this user may edit the shared set. */
  /** Replace a manager's TEAM views (RDS-1.1). `owner` only for an admin editing someone else's. */
  async saveClientTeamViews(views: ClientSavedView[], owner?: string): Promise<ClientTeamViewSet> {
    const response = await this.client.put<{ team: ClientTeamViewSet }>('/client-view/saved-views/team', {
      views,
      ...(owner ? { owner } : {}),
    });
    return response.data.team;
  }

  async getClientSavedViews(signal?: AbortSignal): Promise<ClientSavedViewsResponse> {
    const response = await this.client.get<ClientSavedViewsResponse>('/client-view/saved-views', { signal });
    return response.data;
  }

  /** Find a client, a dealer PARENT group, or a VIN by name / seller id / parent id
   * / VIN fragment. Snapshot-backed for clients, so those with no VINs are findable
   * — the VIN-derived dropdown this replaced could not list them. */
  async searchClients(
    q: string,
    limit = 8,
    signal?: AbortSignal,
  ): Promise<{
    parents: ClientParentSearchResult[];
    clients: ClientSearchResult[];
    vins: ClientVinSearchResult[];
  }> {
    const response = await this.client.get<{
      parents: ClientParentSearchResult[];
      clients: ClientSearchResult[];
      vins: ClientVinSearchResult[];
    }>(
      '/client-view/client-search',
      { params: { q: q || undefined, limit }, signal },
    );
    return {
      parents: response.data.parents || [],
      clients: response.data.clients || [],
      vins: response.data.vins || [],
    };
  }

  /** Replace the current user's PERSONAL Client View saved views (private to them). */
  async saveClientPersonalViews(views: ClientSavedView[]): Promise<ClientSavedView[]> {
    const response = await this.client.put<{ personal: ClientSavedView[] }>('/client-view/saved-views/personal', { views });
    return response.data.personal;
  }

  /** Replace the GLOBAL (shared) Client View saved views. Manager or admin only. */
  async saveClientGlobalViews(views: ClientSavedView[]): Promise<ClientSavedView[]> {
    const response = await this.client.put<{ global: ClientSavedView[] }>('/client-view/saved-views/global', { views });
    return response.data.global;
  }

  /**
   * Clients to call about booking their next inspection. Same filter surface as
   * getClientViewDealers so switching modes keeps the rep's territory.
   */
  async getInspectionCallList(
    params?: CallListParams,
    signal?: AbortSignal,
  ): Promise<CallListResponse> {
    const response = await this.client.get<CallListResponse>('/client-view/call-list', {
      params,
      signal,
    });
    return response.data;
  }

  /** The assignable client statuses (served from the backend so the set can change). */
  async getClientStatusOptions(): Promise<ClientStatusOptionsResponse> {
    const response = await this.client.get<ClientStatusOptionsResponse>('/client-status/options');
    return response.data;
  }

  /** Statuses for a page of clients, with expiry already evaluated. */
  async getClientStatuses(clientIds: string[]): Promise<ClientStatusRecord[]> {
    if (clientIds.length === 0) return [];
    const response = await this.client.get<ClientStatusRecord[]>('/client-status/', {
      params: { client_ids: clientIds.join(',') },
    });
    return response.data;
  }

  /** Assign one status to one or more clients. */
  async setClientStatusBulk(
    clientIds: string[],
    status: string,
    details?: ClientStatusDetails,
  ): Promise<{ success: boolean; updated_count: number }> {
    const response = await this.client.post('/client-status/bulk', {
      client_ids: clientIds,
      status,
      call_back_date: details?.callBackDate ?? null,
      reason_code: details?.reasonCode || null,
      reason_text: details?.reasonText || null,
    });
    return response.data;
  }

  /** Clear a client's status, returning it to the active call list. */
  async clearClientStatus(clientId: string): Promise<ClientStatusRecord> {
    const response = await this.client.delete<ClientStatusRecord>(
      `/client-status/${encodeURIComponent(clientId)}`,
    );
    return response.data;
  }

  /**
   * Get VIN rows for Pop-out 1's table (filtered from the cached VIN_DETAILS).
   */
  async getClientViewDealerVins(sellerId: string, includeDeleted = false): Promise<ClientViewVinsResponse> {
    const response = await this.client.get<ClientViewVinsResponse>(
      `/client-view/dealers/${encodeURIComponent(sellerId)}/vins`,
      { params: includeDeleted ? { include_deleted: true } : undefined },
    );
    return response.data;
  }

  // ========== Broker View Endpoints ==========
  // Independent of Client View: same payload shapes, but the backend gates rows
  // on `broker_action_filter == 'EXCLUDE'` instead of `vin_action_filter`.

  /**
   * Thin map/list payload for the Broker View dealer set (geo + name + tier +
   * priority_counts only). Broker sibling of `getClientViewMapPoints`.
   */
  async getBrokerViewMapPoints(
    params?: ClientViewParams,
    signal?: AbortSignal,
  ): Promise<ClientViewMapPointsResponse> {
    const response = await this.client.get<ClientViewMapPointsResponse>('/broker-view/map-points', { params, signal });
    return response.data;
  }

  /**
   * Per-vehicle map markers for the Broker View map. Each marker aggregates
   * INCLUDE VINs sharing a seller, resolved ZIP and the filter buckets, carrying
   * a vehicle `count`. Drives the map, the severity chips, and the (derived)
   * slim dealer carousel.
   */
  async getBrokerViewVehiclePoints(
    params?: ClientViewParams & { match_types?: string; conditions?: string; conditions_mode?: 'exclude' | 'include'; supply?: string; demand?: string; inspection?: string; broker_status?: string },
    signal?: AbortSignal,
  ): Promise<BrokerVehiclePointsResponse> {
    const response = await this.client.get<BrokerVehiclePointsResponse>('/broker-view/vehicle-points', { params, signal });
    return response.data;
  }

  /**
   * §10-E slice-scoped map: a focused card's (seller | buyer | cluster) targeted
   * board VINs + the candidate buyers for them, geocoded to ZIP centroids.
   * Respects the active board chip filters so the map matches the card/table.
   */
  async getBrokerViewSliceMap(
    slice: 'seller' | 'buyer' | 'cluster',
    key: string,
    params?: ClientViewParams & BrokerChipFilterParams & { match_types?: string },
    signal?: AbortSignal,
  ): Promise<BrokerSliceMapResponse> {
    const response = await this.client.get<BrokerSliceMapResponse>(
      `/broker-view/slice-map/${encodeURIComponent(slice)}/${encodeURIComponent(key)}`,
      { params, signal },
    );
    return response.data;
  }

  /**
   * Batch-fetch dealer card bodies for the visible Broker View carousel page
   * (max 200 ids). The slim broker card only consumes `salesforce_id` from this,
   * to render the Salesforce link.
   */
  async getBrokerViewDealerCards(
    sellerIds: string[],
    signal?: AbortSignal,
  ): Promise<ClientViewDealerCardsResponse> {
    if (sellerIds.length === 0) return { cards: [] };
    const response = await this.client.get<ClientViewDealerCardsResponse>('/broker-view/dealer-cards', {
      params: { ids: sellerIds.join(',') },
      signal,
    });
    return response.data;
  }

  /** Contact emails for a batch of buyer account numbers (outreach copy actions). */
  async getBrokerBuyerEmails(
    buyerUids: string[],
    signal?: AbortSignal,
  ): Promise<Record<string, string>> {
    const uids = Array.from(new Set(buyerUids.filter(Boolean)));
    if (uids.length === 0) return {};
    const response = await this.client.get<{ emails: Record<string, string> }>(
      '/broker-view/buyers/emails',
      { params: { uids: uids.join(',') }, signal },
    );
    return response.data.emails ?? {};
  }

  /**
   * Get full dealer detail for the Broker View pop-out.
   */
  async getBrokerViewDealerDetail(sellerId: string): Promise<ClientViewDealerDetail> {
    const response = await this.client.get<ClientViewDealerDetail>(
      `/broker-view/dealers/${encodeURIComponent(sellerId)}`,
    );
    return response.data;
  }

  /**
   * Get VIN rows for the Broker View pop-out table.
   */
  async getBrokerViewDealerVins(sellerId: string): Promise<ClientViewVinsResponse> {
    const response = await this.client.get<ClientViewVinsResponse>(
      `/broker-view/dealers/${encodeURIComponent(sellerId)}/vins`,
    );
    return response.data;
  }

  /**
   * Ranked, exactly-right buyer recommendations for one stalled VIN. Joins the
   * VIN's vehicle-cohort to the buyer-activity dataset and ranks by match
   * tightness, proximity, buyer strength, MMR retention, recency and engagement.
   */
  async getBrokerViewVinBuyers(
    vin: string,
    limit = 5,
    signal?: AbortSignal,
    maxDistanceMiles?: number,
  ): Promise<BrokerBuyerMatchesResponse> {
    const response = await this.client.get<BrokerBuyerMatchesResponse>(
      `/broker-view/vins/${encodeURIComponent(vin)}/buyers`,
      // §1.3 tolerance is per-request only; omitted means "use the configured default".
      { params: { limit, ...(maxDistanceMiles === undefined ? {} : { max_distance_miles: maxDistanceMiles }) }, signal },
    );
    return response.data;
  }

  /**
   * More inventory to pitch a basket buyer, from the sellers already in that
   * buyer's basket. Draws on the full match pool, so units where the buyer ranks
   * well below the top 5 still surface.
   */
  async getBrokerBasketSuggestions(
    params: {
      buyer_uid: string;
      seller_ids: string;
      scope?: 'any' | 'sellers';
      exclude_vins?: string;
      limit?: number;
      max_distance_miles?: number;
    },
    signal?: AbortSignal,
  ): Promise<{ suggestions: BrokerBasketSuggestion[] }> {
    const response = await this.client.get<{ suggestions: BrokerBasketSuggestion[] }>(
      '/broker-view/basket/suggestions',
      { params, signal },
    );
    return response.data;
  }

  /**
   * Miles from each staged buyer to each of its staged VINs. Per PAIR, because the
   * basket stages one VIN under several buyers and the distance differs for each.
   */
  async getBrokerBasketDistances(
    pairs: { buyer_uid: string; vin: string }[],
    signal?: AbortSignal,
  ): Promise<{ distances: { buyer_uid: string; vin: string; distance_miles: number | null }[] }> {
    if (pairs.length === 0) return { distances: [] };
    const response = await this.client.get<{
      distances: { buyer_uid: string; vin: string; distance_miles: number | null }[];
    }>('/broker-view/basket/distances', {
      params: { pairs: pairs.map((p) => `${p.buyer_uid}:${p.vin}`).join(',') },
      signal,
    });
    return response.data;
  }

  /**
   * The Cluster-tab key for one VIN, so the VIN pop-out can jump to the rest of
   * the comparable units.
   */
  async getBrokerViewVinCluster(
    vin: string,
    signal?: AbortSignal,
  ): Promise<{ cluster_key: string; label: string }> {
    const response = await this.client.get<{ cluster_key: string; label: string }>(
      `/broker-view/vins/${encodeURIComponent(vin)}/cluster`,
      { signal },
    );
    return response.data;
  }

  /**
   * Buyers for one VIN, shared by the Main / Client / Broker pop-outs so all
   * three show the same buyers with the same scoring. Unlike the Broker route
   * this does NOT apply the broker buyer-exclusion list.
   */
  async getVehicleBuyers(
    vin: string,
    limit = 5,
    signal?: AbortSignal,
  ): Promise<BrokerBuyerMatchesResponse> {
    const response = await this.client.get<BrokerBuyerMatchesResponse>(
      `/vehicles/${encodeURIComponent(vin)}/buyers`,
      { params: { limit }, signal },
    );
    return response.data;
  }

  /**
   * Where this VIN's cohort trades — by venue, by channel, and overall. Replaces
   * the retired location/channel same-similar JSON blobs.
   */
  async getVehicleCohortInsights(
    vin: string,
    signal?: AbortSignal,
  ): Promise<CohortInsightsResponse> {
    const response = await this.client.get<CohortInsightsResponse>(
      `/vehicles/${encodeURIComponent(vin)}/cohort-insights`,
      { signal },
    );
    return response.data;
  }

  /**
   * Buyers ranked across the whole filtered board (Buyer-lens pivot). Ranks by
   * how much of the broker's current INCLUDE-VIN work each buyer can move.
   */
  async getBrokerViewBoardBuyers(
    params?: ClientViewParams & BrokerChipFilterParams & { match_types?: string; limit?: number; offset?: number; buyer?: string; sort?: 'reach' | 'lost'; lost_only?: boolean },
    signal?: AbortSignal,
  ): Promise<BrokerBoardBuyersResponse> {
    const response = await this.client.get<BrokerBoardBuyersResponse>(
      '/broker-view/buyers',
      { params, signal },
    );
    return response.data;
  }

  /**
   * One server-paginated page of ALL matching board VINs for a single buyer
   * (the Buyer-lens "view all" drill-down). Kept server-side so we never ship
   * thousands of rows at once.
   */
  async getBrokerViewBuyerVins(
    buyerUid: string,
    params?: ClientViewParams & { limit?: number; offset?: number },
    signal?: AbortSignal,
  ): Promise<BrokerBuyerVinsPageResponse> {
    const response = await this.client.get<BrokerBuyerVinsPageResponse>(
      `/broker-view/buyers/${encodeURIComponent(buyerUid)}/vins`,
      { params, signal },
    );
    return response.data;
  }

  /**
   * Cohort clusters of the filtered board (Cluster-lens pivot). Groups similar
   * INCLUDE VINs so a broker can market a spread of units to a spread of buyers.
   */
  async getBrokerViewBoardClusters(
    params?: ClientViewParams & BrokerChipFilterParams & { match_types?: string; limit?: number; min_size?: number; offset?: number; focus_key?: string },
    signal?: AbortSignal,
  ): Promise<BrokerBoardClustersResponse> {
    const response = await this.client.get<BrokerBoardClustersResponse>(
      '/broker-view/clusters',
      { params, signal },
    );
    return response.data;
  }

  /**
   * One server-paginated page of ALL board VINs in a cluster (the Cluster-lens
   * "view all" drill). ``clusterKey`` is the buyer-match signature.
   */
  async getBrokerViewClusterVins(
    clusterKey: string,
    params?: ClientViewParams & { buyer_uid?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ): Promise<BrokerClusterVinsPageResponse> {
    // The signature contains '|' separators; encode each path segment so the
    // ``:path`` route param round-trips safely.
    const encoded = clusterKey.split('/').map(encodeURIComponent).join('%2F');
    const response = await this.client.get<BrokerClusterVinsPageResponse>(
      `/broker-view/clusters/${encoded}/vins`,
      { params, signal },
    );
    return response.data;
  }

  /**
   * The unified §11 slice VIN table: one server-paginated page of VINs for a
   * `seller` | `buyer` | `cluster` card. Each row carries the shared VIN
   * attributes plus the resolved buyer match (focused buyer on the Buyer tab,
   * the VIN's best buyer on Seller/Cluster). Sort/search/paging are all
   * server-side.
   */
  async getBrokerViewSliceVins(
    slice: BrokerSliceKind,
    key: string,
    params?: ClientViewParams & BrokerChipFilterParams & {
      match_types?: string;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
      limit?: number;
      offset?: number;
    },
    signal?: AbortSignal,
  ): Promise<BrokerSliceVinsResponse> {
    // A cluster key contains '|' separators; encode each path segment so the
    // ``:path`` route param round-trips safely.
    const encoded = key.split('/').map(encodeURIComponent).join('%2F');
    const response = await this.client.get<BrokerSliceVinsResponse>(
      `/broker-view/slice/${encodeURIComponent(slice)}/${encoded}/vins`,
      { params, signal },
    );
    return response.data;
  }

  /** The shared, admin-tunable Broker settings (buyer exclusions + thresholds). */
  async getBrokerSettings(signal?: AbortSignal): Promise<BrokerSettingsResponse> {
    const response = await this.client.get<BrokerSettingsResponse>('/broker-view/settings', { signal });
    return response.data;
  }

  /** Update the shared Broker settings (broker-role or admin). Deep-merges the
   * partial `patch`. Returns the effective settings + whether a recompute kicked. */
  async updateBrokerSettings(patch: Partial<BrokerSettings>): Promise<{ settings: BrokerSettings; recompute: boolean }> {
    const response = await this.client.put('/broker-view/settings', patch);
    return response.data;
  }

  /** §1.1.5 derive urgency weights from a priority order. Rank sets magnitude
   * only; signs are preserved. Returns a patch, never applies it. */
  async rankBrokerWeights(
    orderedKeys: string[],
    topMagnitude?: number,
    directions?: Record<string, string>,
    signal?: AbortSignal,
  ): Promise<BrokerRankedWeights> {
    const response = await this.client.post<BrokerRankedWeights>(
      '/broker-view/settings/rank-weights',
      { ordered_keys: orderedKeys, top_magnitude: topMagnitude, directions },
      { signal },
    );
    return response.data;
  }

  /** §1.1.4 back-solve a tier cutoff from a target vehicle count. Returns a
   * patch to apply, never applies it. */
  async solveBrokerTierCutoff(
    tier: string, targetMin: number, targetMax: number, signal?: AbortSignal,
  ): Promise<BrokerCutoffSolution> {
    const response = await this.client.post<BrokerCutoffSolution>(
      '/broker-view/settings/solve-cutoff',
      { tier, target_min: targetMin, target_max: targetMax },
      { signal },
    );
    return response.data;
  }

  /** §1.1.4 back-solve a tier cutoff from a target SHARE of the board. A bare
   * threshold has no referent; "the top 1%" does. Returns a patch, never applies it. */
  async solveBrokerTierCutoffByShare(
    tier: string, targetShare: number, tolerance?: number, signal?: AbortSignal,
  ): Promise<BrokerCutoffSolution> {
    const response = await this.client.post<BrokerCutoffSolution>(
      '/broker-view/settings/solve-cutoff-share',
      { tier, target_share: targetShare, tolerance },
      { signal },
    );
    return response.data;
  }

  /** §1.1.3 what each urgency control does to today's board, so raw point
   * values have a real-world referent. */
  async getBrokerFactorImpact(signal?: AbortSignal): Promise<BrokerFactorImpactResponse> {
    const response = await this.client.get<BrokerFactorImpactResponse>(
      '/broker-view/settings/factor-impact',
      { signal },
    );
    return response.data;
  }

  /** What each recommendation knob actually does to the top-5, so the panel can
   * show measured effect instead of a raw number that implies nothing. */
  async getBrokerRecommendImpact(signal?: AbortSignal): Promise<BrokerRecommendImpactResponse> {
    const response = await this.client.get<BrokerRecommendImpactResponse>(
      '/broker-view/settings/recommend-impact',
      { signal },
    );
    return response.data;
  }

  /** What each fit component actually does to buyer ranking, as opposed to the
   * weight it was given. */
  async getBrokerFitImpact(signal?: AbortSignal): Promise<BrokerFitImpactResponse> {
    const response = await this.client.get<BrokerFitImpactResponse>(
      '/broker-view/settings/fit-impact',
      { signal },
    );
    return response.data;
  }

  /** Derive the fit blend from a priority order. The weights must total exactly
   * 1.00; deriving them makes that true by construction. Returns a patch. */
  async rankBrokerFitWeights(
    orderedKeys: string[],
    topRatio?: number,
    offKeys?: string[],
    signal?: AbortSignal,
  ): Promise<BrokerFitRankedWeights> {
    const response = await this.client.post<BrokerFitRankedWeights>(
      '/broker-view/settings/fit-rank-weights',
      { ordered_keys: orderedKeys, top_ratio: topRatio, off_keys: offKeys },
      { signal },
    );
    return response.data;
  }

  /** §1.1 dry run: the bucket distribution `patch` WOULD produce, without saving.
   * Never bumps CONFIG_VERSION or triggers a recompute. 422 if the patch is invalid. */
  async previewBrokerSettings(
    patch: Partial<BrokerSettings>,
    signal?: AbortSignal,
  ): Promise<BrokerSettingsPreview> {
    const response = await this.client.post<BrokerSettingsPreview>(
      '/broker-view/settings/preview',
      patch,
      { signal },
    );
    return response.data;
  }

  /** Search buyers (uid + name) for the exclusions picker, biggest buyers first. */
  async searchBrokerBuyers(q?: string, limit = 25, signal?: AbortSignal, vin?: string): Promise<BrokerBuyerSearchResult[]> {
    const response = await this.client.get<{ buyers: BrokerBuyerSearchResult[] }>('/broker-view/buyer-search', {
      params: { q: q || undefined, limit, ...(vin ? { vin } : {}) },
      signal,
    });
    return response.data.buyers;
  }

  /** §3.2 team-entered contact exceptions (Broker settings admin screen). */
  async listBrokerContactOverrides(signal?: AbortSignal): Promise<BrokerContactOverride[]> {
    const response = await this.client.get<{ overrides: BrokerContactOverride[] }>(
      '/broker-view/contact-overrides', { signal });
    return response.data.overrides;
  }

  async setBrokerContactOverride(buyerUid: string, body: {
    channel: string; override_type: string; contact_value?: string | null; reason?: string | null;
  }): Promise<BrokerContactOverride> {
    const response = await this.client.post<BrokerContactOverride>(
      `/broker-view/buyers/${encodeURIComponent(buyerUid)}/contact-overrides`, body);
    return response.data;
  }

  async revokeBrokerContactOverride(
    buyerUid: string, channel: string, contactValue?: string | null,
  ): Promise<void> {
    await this.client.delete(
      `/broker-view/buyers/${encodeURIComponent(buyerUid)}/contact-overrides`,
      { params: { channel, ...(contactValue ? { contact_value: contactValue } : {}) } });
  }

  /** §14.9 search buyer PARENT groups for parent-level exclusions. */
  async searchBrokerParents(q?: string, limit = 25, signal?: AbortSignal): Promise<BrokerParentSearchResult[]> {
    const response = await this.client.get<{ parents: BrokerParentSearchResult[] }>('/broker-view/parent-search', {
      params: { q: q || undefined, limit },
      signal,
    });
    return response.data.parents;
  }

  /** §14.18 search INCLUDE-board sellers (uid + name) for the per-tab entity search. */
  async searchBrokerSellers(q?: string, limit = 15, signal?: AbortSignal): Promise<BrokerSellerSearchResult[]> {
    const response = await this.client.get<{ sellers: BrokerSellerSearchResult[] }>('/broker-view/seller-search', {
      params: { q: q || undefined, limit },
      signal,
    });
    return response.data.sellers;
  }

  /** §14.18 search INCLUDE-board VINs (VIN fragment or make/model) for the per-tab entity search. */
  async searchBrokerVins(q?: string, limit = 15, signal?: AbortSignal): Promise<BrokerVinSearchResult[]> {
    const response = await this.client.get<{ vins: BrokerVinSearchResult[] }>('/broker-view/vin-search', {
      params: { q: q || undefined, limit },
      signal,
    });
    return response.data.vins;
  }

  /** §14.18 unified omnibox — parent groups + buyers + sellers + VINs in ONE request. */
  async searchBrokerBoard(
    q: string,
    limit = 8,
    signal?: AbortSignal,
    scope?: { market_center?: string; auction?: string },
  ): Promise<{
    parents: BrokerSellerGroupSearchResult[];
    buyers: BrokerBuyerSearchResult[];
    sellers: BrokerSellerSearchResult[];
    vins: BrokerVinSearchResult[];
  }> {
    const response = await this.client.get<{
      parents: BrokerSellerGroupSearchResult[];
      buyers: BrokerBuyerSearchResult[];
      sellers: BrokerSellerSearchResult[];
      vins: BrokerVinSearchResult[];
    }>('/broker-view/search', {
      params: {
        q: q || undefined,
        limit,
        market_center: scope?.market_center || undefined,
        auction: scope?.auction || undefined,
      },
      signal,
    });
    return response.data;
  }

  /** §14.10 saved views — the user's PERSONAL set + the GLOBAL shared set, plus
   * whether this user may edit the shared set. */
  async getBrokerSavedViews(signal?: AbortSignal): Promise<BrokerSavedViewsResponse> {
    const response = await this.client.get<BrokerSavedViewsResponse>('/broker-view/saved-views', { signal });
    return response.data;
  }

  /** Replace the current user's PERSONAL saved views (private to them). */
  async saveBrokerPersonalViews(views: BrokerSavedView[]): Promise<BrokerSavedView[]> {
    const response = await this.client.put<{ personal: BrokerSavedView[] }>('/broker-view/saved-views/personal', { views });
    return response.data.personal;
  }

  /** Replace the GLOBAL (shared) saved views. Broker-role or admin only. */
  async saveBrokerGlobalViews(views: BrokerSavedView[]): Promise<BrokerSavedView[]> {
    const response = await this.client.put<{ global: BrokerSavedView[] }>('/broker-view/saved-views/global', { views });
    return response.data.global;
  }

  /** §10-C load the user's persisted outreach basket (private to them). */
  async getBrokerBasket<T = unknown>(signal?: AbortSignal): Promise<T[]> {
    const response = await this.client.get<{ basket: T[] }>('/broker-view/basket', { signal });
    return response.data.basket ?? [];
  }

  /** §10-C persist the user's outreach basket (whole blob; frontend debounces). */
  async saveBrokerBasket<T = unknown>(basket: T[]): Promise<T[]> {
    const response = await this.client.put<{ basket: T[] }>('/broker-view/basket', { basket });
    return response.data.basket ?? [];
  }

  /** §9.8 generate (not send) a copy/paste outreach email for a staged buyer/seller. */
  async draftBrokerOutreach(req: OutreachDraftRequest, signal?: AbortSignal): Promise<OutreachDraftResponse> {
    const response = await this.client.post<OutreachDraftResponse>('/broker-view/outreach/draft', req, { signal });
    return response.data;
  }

  // ========== Agent Endpoints ==========

  /**
   * OPS-1: send a batch of usage events. `keepalive` is for page hide/unload, where an axios
   * request would be cancelled: fetch with keepalive outlives the page and still carries the
   * auth headers (sendBeacon cannot). Errors are the caller's to swallow.
   */
  async sendUsageEvents(events: UsageEventPayload[], keepalive = false): Promise<void> {
    if (!keepalive) {
      await this.client.post('/usage/events', { events });
      return;
    }
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.accessToken) {
      headers[ENTRA_AUTH_HEADER] = `Bearer ${this.accessToken}`;
      headers.Authorization = `Bearer ${this.accessToken}`;
    } else {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    await fetch(`${API_BASE_URL}/usage/events`, {
      method: 'POST', headers, credentials: 'include', keepalive: true, body: JSON.stringify({ events }),
    });
  }

  /**
   * Stream one agent turn over Server-Sent Events.
   *
   * Uses fetch + ReadableStream (not EventSource) so the request can be a POST that carries the
   * message/selection body and the Bearer token. `onEvent` is invoked once per parsed SSE event;
   * the returned promise resolves when the stream ends. Pass an AbortSignal to cancel.
   */
  async streamAgentChat(
    body: AgentChatRequest,
    onEvent: (event: AgentStreamEvent) => void,
    signal?: AbortSignal,
  ): Promise<void> {
    const token = this.accessToken ?? localStorage.getItem(TOKEN_STORAGE_KEY);
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    const response = await fetch(`${API_BASE_URL}/agent/chat`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: agentChatBody(body),
      signal,
    });

    if (!response.ok || !response.body) {
      let detail = `Agent request failed (${response.status})`;
      try {
        const data = await response.json();
        if (data?.detail) detail = data.detail;
      } catch {
        // Our API always answers in JSON, so a non-JSON 403 is the WAF in front of it.
        if (response.status === 403) detail = AGENT_WAF_BLOCK_MESSAGE;
      }
      throw new Error(detail);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // SSE frames are separated by a blank line. A frame may contain multiple `data:`/comment lines.
    const flushFrame = (frame: string) => {
      const dataLines = frame
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trim());
      if (dataLines.length === 0) return; // heartbeat comment or empty
      const payload = dataLines.join('');
      try {
        onEvent(JSON.parse(payload) as AgentStreamEvent);
      } catch {
        /* ignore malformed frame */
      }
    };

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        flushFrame(frame);
      }
    }
    buffer += decoder.decode();
    if (buffer.trim()) flushFrame(buffer);
  }

  /**
   * End the current conversation: the backend archives it to long-term storage (under the given
   * conversation_id) and clears the login session's working memory.
   */
  async resetAgentChat(conversationId?: string): Promise<void> {
    await this.client.post('/agent/reset', { conversation_id: conversationId });
  }

  /**
   * Record a passive good/bad rating on one agent response, or clear it with rating=null. Targets
   * the turn by the server-issued turn_id from that turn's `done` event. Best-effort.
   */
  async sendAgentFeedback(
    conversationId: string,
    turnId: string,
    rating: AgentFeedbackRating | null,
  ): Promise<void> {
    await this.client.post('/agent/feedback', {
      conversation_id: conversationId,
      turn_id: turnId,
      rating,
    });
  }

  // ========== Sales View Endpoints (CSE / Dealer Sales) ==========

  /**
   * Repeat array params as `client_token=a&client_token=b`, which is what
   * FastAPI's `List[str] = Query(...)` reads. Axios defaults to `client_token[]=a`,
   * and the bracket makes the whole list invisible to the server.
   */
  private static readonly REPEAT_ARRAYS = { indexes: null } as const;

  /**
   * Ranked client list, worst health first.
   *
   * Territory scoping travels as ordinary filter values in `params`, seeded from
   * `getSalesViewFilterOptions().defaults` -- the server applies no hidden
   * restriction, so omitting them returns the whole book.
   */
  async getSalesViewClients(
    params?: SalesViewParams,
    signal?: AbortSignal,
  ): Promise<SalesViewClientsResponse> {
    const response = await this.client.get<SalesViewClientsResponse>('/sales-view/clients', {
      params, signal, paramsSerializer: SiftApiClient.REPEAT_ARRAYS,
    });
    return response.data;
  }

  /** Drawer payload: windows, channel mix, SFDC block, buy profile, score provenance. */
  async getSalesViewClientDetail(
    clientId: string,
    impersonateRole?: string,
    signal?: AbortSignal,
  ): Promise<SalesViewClientDetail> {
    const response = await this.client.get<SalesViewClientDetail>(
      `/sales-view/clients/${encodeURIComponent(clientId)}`,
      { params: { impersonate_role: impersonateRole }, signal },
    );
    return response.data;
  }

  /** Mia's 2-3 sentence read of the client. Seconds to generate on a miss; cached server-side. */
  async getSalesClientSummary(
    clientId: string,
    impersonateRole?: string,
    signal?: AbortSignal,
  ): Promise<SalesClientSummary> {
    const response = await this.client.get<SalesClientSummary>(
      `/sales-view/clients/${encodeURIComponent(clientId)}/summary`,
      { params: { impersonate_role: impersonateRole }, signal, timeout: 120_000 },
    );
    return response.data;
  }

  /** Mia's "Where to focus" for a Client View dealer (`client`, seller UID) or unit (`vin`). */
  async getRdsSummary(
    kind: 'client' | 'vin', id: string, signal?: AbortSignal,
  ): Promise<{ text: string; generated_at?: string; cached?: boolean }> {
    const path = kind === 'client'
      ? `/client-view/dealers/${encodeURIComponent(id)}/summary`
      : `/client-view/vins/${encodeURIComponent(id)}/summary`;
    const response = await this.client.get<{ text: string; generated_at?: string; cached?: boolean }>(
      path, { signal, timeout: 120_000 },
    );
    return response.data;
  }

  /** Seed a "Where to focus" summary, and the record behind it, into the rep's RDS Mia thread. */
  async continueRdsSummary(
    kind: 'client' | 'vin', id: string, conversationId?: string,
  ): Promise<{ prompt: string; reply: string }> {
    const response = await this.client.post<{ prompt: string; reply: string }>(
      '/agent/rds-summary/continue', { kind, key: id, conversation_id: conversationId },
    );
    return response.data;
  }

  /** Seed the client summary, and the record behind it, into the rep's Sales Mia thread. */
  async continueClientSummary(
    clientId: string, conversationId?: string,
  ): Promise<{ prompt: string; reply: string }> {
    const response = await this.client.post<{ prompt: string; reply: string }>(
      '/agent/client-summary/continue', { client_id: clientId, conversation_id: conversationId },
    );
    return response.data;
  }

  /** Peer-cohort baselines. Split off the detail call because it is heavier and
   *  the drawer section is lazy-loaded. */
  async getSalesViewClientCohort(
    clientId: string,
    impersonateRole?: string,
    signal?: AbortSignal,
    options?: { basis?: 'territory' | 'brand'; radiusMi?: number },
  ): Promise<SalesViewCohort> {
    const response = await this.client.get<SalesViewCohort>(
      `/sales-view/clients/${encodeURIComponent(clientId)}/cohort`,
      {
        params: {
          impersonate_role: impersonateRole,
          basis: options?.basis,
          radius_mi: options?.basis === 'brand' ? options.radiusMi : undefined,
        },
        signal,
      },
    );
    return response.data;
  }

  /** What the client buys and sells, by auction and price band (S1.15). Served
   *  from the daily Parquet, so the window toggle is cheap to flip. */
  async getSalesViewClientPriceBands(
    clientId: string,
    windowMonths: 3 | 12,
    impersonateRole?: string,
    signal?: AbortSignal,
    bandKind: BandKind = 'price',
  ): Promise<SalesPriceBandsBlock> {
    const response = await this.client.get<SalesPriceBandsBlock>(
      `/sales-view/clients/${encodeURIComponent(clientId)}/price-bands`,
      {
        params: { window_months: windowMonths, band_kind: bandKind, impersonate_role: impersonateRole },
        signal,
      },
    );
    return response.data;
  }

  /** EVERY auction, all clients (S1.15). Not client-scoped — answers "what does
   *  that lane look like" for a lane the client has never traded at. */
  async getSalesViewMarketBands(
    windowMonths: 3 | 12,
    impersonateRole?: string,
    signal?: AbortSignal,
    bandKind: BandKind = 'price',
  ): Promise<SalesMarketBandsBlock> {
    const response = await this.client.get<SalesMarketBandsBlock>(
      '/sales-view/auction-bands',
      {
        params: { window_months: windowMonths, band_kind: bandKind, impersonate_role: impersonateRole },
        signal,
      },
    );
    return response.data;
  }

  /** Tier / reason / engagement counts for the CURRENT filter set. Takes the same
   *  filters as `getSalesViewClients` so the chip counts agree with the list. */
  async getSalesViewSummary(
    params?: SalesViewParams,
    signal?: AbortSignal,
  ): Promise<SalesViewSummary> {
    const response = await this.client.get<SalesViewSummary>('/sales-view/summary', {
      params, signal, paramsSerializer: SiftApiClient.REPEAT_ARRAYS,
    });
    return response.data;
  }

  /** Thin geo payload: tier fill plus aged-listing halo severity. */
  async getSalesViewMapPoints(
    params?: SalesViewParams,
    signal?: AbortSignal,
  ): Promise<SalesViewMapPointsResponse> {
    const response = await this.client.get<SalesViewMapPointsResponse>('/sales-view/map-points', {
      params, signal, paramsSerializer: SiftApiClient.REPEAT_ARRAYS,
    });
    return response.data;
  }

  /**
   * Typeahead for the Sales View client filter.
   *
   * NOT `searchClients`, which reads the 139k-dealer seller snapshot: the sales
   * book holds 40.8k CSE clients, so that endpoint would offer a rep dealers
   * Sales View has no row for and the board would come back empty.
   */
  async searchSalesClients(
    q: string,
    limit = 8,
    signal?: AbortSignal,
  ): Promise<{ parents: SalesParentSearchResult[]; clients: SalesClientSearchResult[] }> {
    const response = await this.client.get<{
      parents: SalesParentSearchResult[];
      clients: SalesClientSearchResult[];
    }>('/sales-view/client-search', { params: { q, limit }, signal });
    return response.data;
  }

  /** Full option lists plus this user's role defaults (empty for admin). */
  async getSalesViewFilterOptions(
    impersonateRole?: string,
    signal?: AbortSignal,
  ): Promise<SalesFilterOptions> {
    const response = await this.client.get<SalesFilterOptions>('/sales-view/filter-options', {
      params: { impersonate_role: impersonateRole },
      signal,
    });
    return response.data;
  }

  /** CSE-17 leader target lists, plus whether this user may change them. */
  async getSalesTargetLists(impersonateRole?: string): Promise<SalesTargetListsResponse> {
    const response = await this.client.get<SalesTargetListsResponse>('/sales-view/target-lists', {
      params: { impersonate_role: impersonateRole },
    });
    return response.data;
  }

  /** Pasted account numbers (spaces, lines or commas) and a free-form reason. */
  async createSalesTargetList(reason: string, idsText: string): Promise<SalesTargetListCreateResponse> {
    const response = await this.client.post<SalesTargetListCreateResponse>(
      '/sales-view/target-lists', { reason, ids_text: idsText },
    );
    return response.data;
  }

  async deleteSalesTargetList(listId: string): Promise<void> {
    await this.client.delete(`/sales-view/target-lists/${encodeURIComponent(listId)}`);
  }

  /** Take clients off one list, or off every list when `listId` is omitted. */
  async untargetSalesClients(idsText: string, listId?: string): Promise<SalesTargetUntargetResponse> {
    const response = await this.client.post<SalesTargetUntargetResponse>(
      '/sales-view/target-lists/untarget', { ids_text: idsText, list_id: listId ?? null },
    );
    return response.data;
  }

  /** Close-out action: contacted / follow_up / dismissed. */
  async setSalesViewClientStatus(
    clientId: string,
    payload: SalesCloseOutRequest,
  ): Promise<SalesCloseOutResponse> {
    const response = await this.client.post<SalesCloseOutResponse>(
      `/sales-view/clients/${encodeURIComponent(clientId)}/status`,
      payload,
    );
    return response.data;
  }

  /** The 15 signals a client COULD trip, with live weights. Book-independent and
   *  version-stamped, so the drawer can show the whole 32-point budget instead of
   *  only the signals that fired. */
  async getSalesViewScoreSignals(signal?: AbortSignal): Promise<SalesScoreCatalog> {
    const response = await this.client.get<SalesScoreCatalog>('/sales-view/score-signals', { signal });
    return response.data;
  }

  // -------------------------------------------------------------------------
  // Plan My Week
  //
  // `rep` is REQUIRED above rep level: the backend 400s a manager who names no
  // rep rather than blending 200 territories into a plan nobody can drive.
  // `week` is an ISO Monday; omitting it means the current week.
  // -------------------------------------------------------------------------

  /** The recommendation with this rep's overrides applied. Check `source` before
   *  showing adherence — `generated_on_read` means there is no frozen baseline. */
  async getSalesPlanCurrent(
    params?: { rep?: string; week?: string; impersonate_role?: string },
    signal?: AbortSignal,
  ): Promise<SalesPlanCurrentResponse> {
    const response = await this.client.get<SalesPlanCurrentResponse>('/sales-view/plan/current', { params, signal });
    return response.data;
  }

  /** What the rep kept, dropped, added and moved. */
  async getSalesPlanDiff(
    params?: { rep?: string; week?: string; impersonate_role?: string },
    signal?: AbortSignal,
  ): Promise<SalesPlanDiffResponse> {
    const response = await this.client.get<SalesPlanDiffResponse>('/sales-view/plan/diff', { params, signal });
    return response.data;
  }

  /** What moved since Monday. Read-only: it proposes, the rep disposes. */
  async getSalesPlanDigest(
    params?: { rep?: string; week?: string; impersonate_role?: string },
    signal?: AbortSignal,
  ): Promise<SalesPlanDigestResponse> {
    const response = await this.client.get<SalesPlanDigestResponse>('/sales-view/plan/digest', { params, signal });
    return response.data;
  }

  /** Paste-ready recap, confirmed work kept separate from work not yet in SFDC. */
  async getSalesPlanRecap(
    params?: { rep?: string; week?: string; impersonate_role?: string },
    signal?: AbortSignal,
  ): Promise<SalesPlanRecapResponse> {
    const response = await this.client.get<SalesPlanRecapResponse>('/sales-view/plan/recap', { params, signal });
    return response.data;
  }

  /** Context for a curated Mia thread, so the opening move is *review and adjust*
   *  rather than a cold "plan my week" typed into an empty box. */
  async getSalesPlanKickoff(
    params?: { rep?: string; week?: string; impersonate_role?: string },
    signal?: AbortSignal,
  ): Promise<SalesPlanKickoffResponse> {
    const response = await this.client.get<SalesPlanKickoffResponse>('/sales-view/plan/kickoff', { params, signal });
    return response.data;
  }

  /** Can we actually email this person, and if not, exactly why. Run it BEFORE a
   *  pilot: a rep who never receives mail cannot tell that apart from a broken
   *  batch, a missed opt-in, or a plan that was never generated. */
  async checkSalesPlanEmail(
    params?: { recipient?: string; impersonate_role?: string },
    signal?: AbortSignal,
  ): Promise<Record<string, unknown>> {
    const response = await this.client.get<Record<string, unknown>>('/sales-view/plan/email-check', { params, signal });
    return response.data;
  }

  /** Who else the rep could see, given where this week already takes them.
   *
   *  With a `trip`, ordered by distance from the nearest stop on it — the question a
   *  rep asks standing in a car park with an hour back. Without one it is the CALL
   *  list, which has no geography, so it comes back ranked and with `miles` null. */
  async getSalesPlanNearby(
    params?: { rep?: string; week?: string; trip?: number; limit?: number; impersonate_role?: string },
    signal?: AbortSignal,
  ): Promise<SalesPlanNearbyResponse> {
    const response = await this.client.get<SalesPlanNearbyResponse>('/sales-view/plan/nearby', { params, signal });
    return response.data;
  }

  /** Record one override. `source` is required — a mutation with no source
   *  cannot be back-filled, because the information was never captured. */
  async addSalesPlanEdit(
    payload: PlanEdit,
    params?: { rep?: string; week?: string; impersonate_role?: string },
  ): Promise<PlanEditsResponse> {
    const response = await this.client.post<PlanEditsResponse>('/sales-view/plan/edits', payload, { params });
    return response.data;
  }

  /** Fix an account as always a call or a visit, or clear it with `mode: null` (CSE-24).
   *  Written to the REP's settings; applied to this week as well as every later build. */
  async setSalesPlanAccountMode(
    clientId: string,
    mode: 'VISIT' | 'CALL' | null,
    params?: { rep?: string; week?: string; impersonate_role?: string },
  ): Promise<{ account_modes: Record<string, 'VISIT' | 'CALL'> }> {
    const response = await this.client.post('/sales-view/plan/account-mode',
      { client_id: clientId, mode }, { params });
    return response.data;
  }

  /** Reset to the recommendation. The plan of record is untouched; only the
   *  overrides are dropped. */
  async resetSalesPlanEdits(
    params?: { rep?: string; week?: string; impersonate_role?: string },
  ): Promise<PlanEditsResponse> {
    const response = await this.client.delete<PlanEditsResponse>('/sales-view/plan/edits', { params });
    return response.data;
  }

  /** Send this week's plan to the CALLER now. The backend never accepts a recipient:
   *  mailing an arbitrary address a rep's client list is an open relay with a
   *  territory attached. Returns `sent: false` WITH a reason rather than throwing. */
  async emailSalesPlan(
    params?: { rep?: string; week?: string; kind?: 'weekly' | 'digest'; impersonate_role?: string },
  ): Promise<{ sent: boolean; recipient: string; reason?: string; blocked_by?: string }> {
    const response = await this.client.post('/sales-view/plan/email', null, { params });
    return response.data;
  }

  /** ONE summary covering several reps, to the caller. Never freezes a rep's week. */
  async emailSalesPlanSummary(
    body: { reps: string[]; kind: 'weekly' | 'digest' },
    params?: { week?: string; impersonate_role?: string },
  ): Promise<{
    sent: boolean; recipient: string; reps: number; reason?: string; blocked_by?: string;
  }> {
    const response = await this.client.post('/sales-view/plan/email-summary', body, { params });
    return response.data;
  }

  /** The opt-ins and capacity. Naming a `rep` reads THEIR settings, which is how a
   *  manager sees the capacity their rep is actually being planned against. */
  async getSalesPlanSettings(
    params?: { rep?: string; impersonate_role?: string },
    signal?: AbortSignal,
  ): Promise<PlanSettings> {
    const response = await this.client.get<PlanSettings>('/sales-view/plan/settings', { params, signal });
    return response.data;
  }

  async setSalesPlanSetting(
    key: 'weekly_email' | 'daily_digest' | 'capacity' | 'subscribed_reps' | 'default_trip_start',
    value: unknown,
    params?: { rep?: string; impersonate_role?: string },
  ): Promise<PlanSettings> {
    const response = await this.client.post<PlanSettings>('/sales-view/plan/settings', { key, value }, { params });
    return response.data;
  }

  /** Save a trip starting point. Geocoded by the backend; a 422 means it could not be found. */
  async addSalesPlanStartAddress(
    body: { label: string; address: string },
    params?: { rep?: string; impersonate_role?: string },
  ): Promise<{ start_addresses: StartAddress[] }> {
    const response = await this.client.post('/sales-view/plan/start-addresses', body, { params });
    return response.data;
  }

  async removeSalesPlanStartAddress(
    id: string,
    params?: { rep?: string; impersonate_role?: string },
  ): Promise<{ start_addresses: StartAddress[] }> {
    const response = await this.client.delete(
      `/sales-view/plan/start-addresses/${encodeURIComponent(id)}`, { params },
    );
    return response.data;
  }

  /** The roads each trip is driven on, [lat, lon] per point. `line: null` = draw it straight. */
  async getSalesPlanRouteShapes(
    trips: Array<{ trip_no: number; points: Array<[number, number]> }>,
  ): Promise<{ trips: Array<{ trip_no: number; line: Array<[number, number]> | null }> }> {
    const response = await this.client.post('/sales-view/plan/route-shapes', { trips });
    return response.data;
  }
}

// Export a singleton instance
export const siftApiClient = new SiftApiClient();

// Export the class for testing purposes
export default SiftApiClient;
